import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '@/components/common/Button';
import Card, { CardContent, CardHeader, CardTitle, CardFooter } from '@/components/common/Card';
import { User, Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import { logger } from '@/lib/logger';

// Esquema de validación con Zod
const registerSchema = z
  .object({
    username: z
      .string()
      .min(1, 'El nombre de usuario es requerido')
      .min(3, 'El nombre de usuario debe tener al menos 3 caracteres')
      .max(50, 'El nombre de usuario no puede exceder 50 caracteres')
      .regex(
        /^[a-zA-Z0-9_]+$/,
        'Solo se permiten letras, números y guiones bajos'
      ),
    email: z
      .string()
      .min(1, 'El correo electrónico es requerido')
      .email('Correo electrónico no válido'),
    nombre: z
      .string()
      .min(1, 'El nombre completo es requerido')
      .min(3, 'El nombre debe tener al menos 3 caracteres'),
    password: z
      .string()
      .min(1, 'La contraseña es requerida')
      .min(6, 'La contraseña debe tener al menos 6 caracteres')
      .regex(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'La contraseña debe contener al menos una mayúscula, una minúscula y un número'
      ),
    confirmPassword: z
      .string()
      .min(1, 'Debes confirmar tu contraseña'),
    acceptTerms: z
      .boolean()
      .refine((val) => val === true, {
        message: 'Debes aceptar los términos y condiciones',
      }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

const Register = () => {
  const { register: registerUser, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      clearError();
      const { confirmPassword, acceptTerms, ...registerData } = data;
      await registerUser(registerData);
    } catch (error) {
      logger.error('Error al registrar:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-background dark:bg-gray-900 dark:text-gray-100 p-4 animate-fadeIn">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
            <div className="mx-auto h-20 w-20 mb-4">
                <img
                  src="/logo.png"
                  alt="MundiRep"
                  className="w-full h-full object-contain"
                />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground">
                Crear Cuenta
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
                Únete al Sistema Mundi para gestionar tus cotizaciones
            </p>
        </div>

        <Card className="border-border/50 shadow-2xl shadow-primary/5 backdrop-blur-sm bg-card/90">
          <CardHeader>
            <CardTitle className="text-center text-lg">Formulario de Registro</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-start space-x-3">
                   <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                   <p className="text-sm text-destructive font-medium">{error}</p>
                </div>
              )}

              {/* Nombre Completo */}
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none" htmlFor="nombre">
                  Nombre Completo
                </label>
                <div className="relative">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input
                      {...register('nombre')}
                      id="nombre"
                      type="text"
                      className={`flex h-11 md:h-10 w-full rounded-md border bg-background px-3 py-2 pl-9 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        errors.nombre ? 'border-destructive focus-visible:ring-destructive' : 'border-input'
                      }`}
                      placeholder="Juan Pérez"
                    />
                </div>
                {errors.nombre && (
                  <p className="text-xs text-destructive font-medium">{errors.nombre.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Username */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none" htmlFor="username">
                      Usuario
                    </label>
                    <div className="relative">
                        <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <input
                          {...register('username')}
                          id="username"
                          type="text"
                          className={`flex h-11 md:h-10 w-full rounded-md border bg-background px-3 py-2 pl-9 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                            errors.username ? 'border-destructive focus-visible:ring-destructive' : 'border-input'
                          }`}
                          placeholder="juanperez"
                        />
                    </div>
                    {errors.username && (
                      <p className="text-xs text-destructive font-medium">{errors.username.message}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none" htmlFor="email">
                      Email
                    </label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <input
                          {...register('email')}
                          id="email"
                          type="email"
                          className={`flex h-11 md:h-10 w-full rounded-md border bg-background px-3 py-2 pl-9 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                            errors.email ? 'border-destructive focus-visible:ring-destructive' : 'border-input'
                          }`}
                          placeholder="juan@ejemplo.com"
                        />
                    </div>
                    {errors.email && (
                      <p className="text-xs text-destructive font-medium">{errors.email.message}</p>
                    )}
                  </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Password */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none" htmlFor="password">
                      Contraseña
                    </label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <input
                          {...register('password')}
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          className={`flex h-11 md:h-10 w-full rounded-md border bg-background px-3 py-2 pl-9 pr-10 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                            errors.password ? 'border-destructive focus-visible:ring-destructive' : 'border-input'
                          }`}
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-3 md:top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5 md:h-4 md:w-4" /> : <Eye className="h-5 w-5 md:h-4 md:w-4" />}
                        </button>
                    </div>
                    {errors.password && (
                      <p className="text-xs text-destructive font-medium">{errors.password.message}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium leading-none" htmlFor="confirmPassword">
                      Confirmar
                    </label>
                    <div className="relative">
                        <Lock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                        <input
                          {...register('confirmPassword')}
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          className={`flex h-11 md:h-10 w-full rounded-md border bg-background px-3 py-2 pl-9 pr-10 text-base md:text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                            errors.confirmPassword ? 'border-destructive focus-visible:ring-destructive' : 'border-input'
                          }`}
                          placeholder="••••••••"
                        />
                         <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-3 md:top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5 md:h-4 md:w-4" /> : <Eye className="h-5 w-5 md:h-4 md:w-4" />}
                        </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-xs text-destructive font-medium">{errors.confirmPassword.message}</p>
                    )}
                  </div>
              </div>

              {/* Accept terms */}
              <div className="flex items-start space-x-2 pt-2">
                <div className="flex items-center h-5">
                   <input
                      {...register('acceptTerms')}
                      id="acceptTerms"
                      type="checkbox"
                      className="h-4 w-4 rounded border-primary text-primary focus:ring-primary"
                    />
                </div>
                <div className="text-sm">
                    <label htmlFor="acceptTerms" className="text-muted-foreground font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Acepto los <a href="#" className="text-primary hover:underline underline-offset-4">términos y condiciones</a>
                    </label>
                    {errors.acceptTerms && (
                      <p className="text-xs text-destructive font-medium mt-1">{errors.acceptTerms.message}</p>
                    )}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full mt-4"
                size="lg"
                loading={isLoading}
              >
                Crear Cuenta
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex justify-center border-t border-border pt-6 mt-2">
            <p className="text-sm text-muted-foreground">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="font-medium text-primary hover:underline underline-offset-4">
                Inicia sesión aquí
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Register;